import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { formatLabel } from '@repo/locale-client';
import { captureException } from '../lazySentry';
import ErrorPage from '../pages/ErrorPage';
import ChunkErrorPage from '../pages/ChunkErrorPage';
import NetworkErrorPage from '../pages/NetworkErrorPage';
import { isChunkLoadError, handleChunkLoadError } from '../utils/lazyWithRetry';
import { isNetworkError } from '../utils/isNetworkError';
import { getLabels } from '../labels';
/**
 * Error component for React Router's errorElement.
 * Handles different error types with appropriate UI:
 * - Chunk load errors: auto-refresh page (deployment issues)
 * - Network errors: show NetworkErrorPage (user can report manually)
 * - HTTP errors (404, etc.): show ErrorPage with status
 * - Other errors: show generic ErrorPage
 *
 * Network errors are NOT auto-reported to Sentry.
 */
export default function RootRouteError() {
    const error = useRouteError();
    const capturedRef = useRef(false);
    const labels = getLabels();
    const chunkLoadError = isChunkLoadError(error);
    const networkError = isNetworkError(error);
    useEffect(() => {
        if (capturedRef.current)
            return;
        capturedRef.current = true;
        // Handle chunk load errors with auto-refresh
        if (chunkLoadError) {
            const refreshed = handleChunkLoadError();
            if (refreshed)
                return;
        }
        // Don't auto-report network errors - user can report manually
        if (networkError)
            return;
        captureException(error);
    }, [error, chunkLoadError, networkError]);
    // Chunk load error - show refresh page
    if (chunkLoadError) {
        return _jsx(ChunkErrorPage, {});
    }
    // Network error - show dedicated page with manual report option
    if (networkError) {
        const networkError = error instanceof Error ? error : new Error(String(error));
        const handleRetry = () => {
            if (window.history.length > 1) {
                window.history.back();
                return;
            }
            window.location.href = '/';
        };
        return _jsx(NetworkErrorPage, { error: networkError, onRetry: handleRetry });
    }
    // HTTP response error (404, 500, etc.)
    if (isRouteErrorResponse(error)) {
        const message = error.status === 404 ? labels.http['404'] : formatLabel(labels.http.default, { status: error.status });
        const data = typeof error.data === 'string' ? error.data : JSON.stringify(error.data, null, 2);
        return _jsx(ErrorPage, { source: "react", message: message, details: data });
    }
    // Generic error
    const message = error instanceof Error ? error.message : labels.common.unknown;
    const stack = error instanceof Error ? error.stack : undefined;
    return _jsx(ErrorPage, { source: "route", message: message, details: stack });
}
